/**
 * Aufzählungen entstehen beim Tippen — „- " und „1. " werden zur Liste.
 *
 * Die Erwartung kommt aus Word und Outlook: Wer eine Zeile mit einem Strich
 * oder einer Zahl beginnt und die Leertaste drückt, bekommt eine eingerückte
 * Liste, die selbstständig weiterzählt. Vorher musste man im KMVS-Editor den
 * Knopf in der Werkzeugleiste finden — und wer stattdessen weitertippte, hatte
 * am Ende eine Pseudo-Liste aus Bindestrichen ohne jede Einrückung.
 *
 * Alles Wissen über das WANN liegt hier, pur und ohne DOM. Der Editor macht
 * daraus das WAS (`execCommand`), und diese Trennung ist der Grund, warum die
 * Regeln überhaupt prüfbar sind: Ob „1." am Zeilenanfang eine Liste beginnt,
 * lässt sich als Frage an eine Funktion stellen, nicht an einen Browser.
 */

/** Welche Liste ein Marker beginnt. */
export type ListenArt = "ul" | "ol";

/** Ein erkannter Marker: Listenart und — bei Nummerierungen — die getippte Startzahl. */
export type ListenMarker = { art: ListenArt; start: number };

/**
 * Beginnt der Text vor dem Cursor eine Liste?
 *
 * Erlaubt sind `-` und `*` (Aufzählung) sowie `1.` / `1)` (Nummerierung),
 * jeweils AM ZEILENANFANG und unmittelbar vor dem Cursor — führende
 * Leerzeichen zählen nicht als Text. Steht irgendetwas anderes davor, ist es
 * kein Marker, sondern ein Bindestrich mitten im Satz.
 *
 * Bewusst NICHT erlaubt: `+` (steht in Mails zu oft für „und"), Buchstaben-
 * Aufzählungen wie `a)` (kollidieren mit gängigen Abkürzungen) und Marker
 * ohne Zahl wie `.` oder `)`.
 */
export function erkenneListenMarker(textVorCursor: string): ListenMarker | null {
    // Kein Zeilenumbruch im Text vor dem Cursor: Was hier ankommt, ist bereits
    // der Anfang des aktuellen Absatzes (der Aufrufer schneidet ihn zu).
    const roh = textVorCursor.replace(/\u00A0/g, " ");
    if (/^[ \t]*[-*]$/.test(roh)) return { art: "ul", start: 1 };
    const zahl = /^[ \t]*(\d{1,3})[.)]$/.exec(roh);
    // T-347: Die getippte Zahl ist die Startnummer — „2. " beginnt die Liste
    // bei 2, wie in Word und Outlook. „0." zählt als 1 (keine Liste beginnt bei 0).
    if (zahl) return { art: "ol", start: Math.max(1, Number(zahl[1])) };
    return null;
}

/**
 * Ist der aktuelle Listenpunkt leer? Dann beendet Enter die Liste, statt einen
 * weiteren leeren Punkt anzuhängen.
 *
 * `<br>`-Reste und geschützte Leerzeichen zählen als leer — ein frisch
 * angelegter Listenpunkt enthält je nach Browser genau das.
 */
export function istListenpunktLeer(text: string): boolean {
    return text.replace(/\u00A0/g, " ").replace(/[\u200B\uFEFF]/g, "").trim() === "";
}

/**
 * Der nächstgelegene Listenpunkt über einem Knoten — oder `null`, wenn der
 * Cursor gar nicht in einer Liste steht.
 *
 * Die Suche endet an `root`: Ein `<li>` AUSSERHALB des Editors (der Editor
 * kann in einer Listenansicht stecken) darf niemals als „aktueller
 * Listenpunkt" durchgehen.
 */
export function findeListenpunkt(start: Node | null, root: Node): HTMLElement | null {
    let n: Node | null = start;
    while (n && n !== root) {
        if (n.nodeType === 1 && (n as HTMLElement).tagName === "LI") return n as HTMLElement;
        n = n.parentNode;
    }
    return null;
}

/**
 * Text des aktuellen Absatzes VOR dem Cursor.
 *
 * Gebraucht wird nur, was seit dem letzten Zeilenanfang getippt wurde — der
 * Editor ist ein einziges `contentEditable`, in dem Absätze als `<div>`,
 * `<p>` oder als Text mit `<br>` dazwischen vorliegen können, je nachdem, wie
 * der Browser gerade schreibt. Deshalb wird am nächsten Block-Element bzw. am
 * letzten `<br>` abgeschnitten.
 */
export function textVorCursorInZeile(container: Node, offset: number, root: Node): string {
    if (container.nodeType !== 3) return "";
    const eigener = (container.textContent ?? "").slice(0, offset);
    if (eigener.includes("\n")) return eigener.slice(eigener.lastIndexOf("\n") + 1);

    // Vorherige Geschwister bis zum Blockanfang bzw. bis zum letzten <br>.
    let davor = "";
    let n: Node | null = container;
    while (n && n !== root) {
        let s: Node | null = n.previousSibling;
        while (s) {
            if (s.nodeType === 1) {
                const el = s as HTMLElement;
                if (el.tagName === "BR") return davor + eigener;
                if (istBlock(el)) return davor + eigener;
            }
            davor = (s.textContent ?? "") + davor;
            s = s.previousSibling;
        }
        n = n.parentNode;
        if (n && n.nodeType === 1 && istBlock(n as HTMLElement)) break;
    }
    return davor + eigener;
}

const BLOCK_TAGS = new Set([
    "DIV", "P", "LI", "UL", "OL", "BLOCKQUOTE", "TABLE", "TR", "TD", "TH",
    "H1", "H2", "H3", "H4", "H5", "H6", "PRE",
]);

function istBlock(el: HTMLElement): boolean {
    return BLOCK_TAGS.has(el.tagName);
}
